/**
 * routes.tsx — Theme A of the Pre-Testing Remediation Pass.
 *
 * Centralizes lazy-loaded route components for organizer surfaces
 * (Phase 3 PARA pages), trust/transparency surfaces, and admin pages.
 * Keeps App.tsx focused on auth + shell wiring.
 *
 * Each component renders inside a `<ShellLayout>` wrapper that mounts
 * the global Cmd+K command palette so navigation works everywhere.
 *
 * Routes grouped:
 *   /today /inbox /upcoming /areas /projects /comms /canvas
 *     — Phase 3 organizer surfaces
 *   /transparency/{decisions,retrospective,autonomy,operator-model}
 *     — Trust surfaces (read-only)
 *   /admin/<page>
 *     — Admin / operator-secondary pages. Distinct from the legacy
 *       /admin/<alias> map in settingsCatalog.tsx; the redirect
 *       catch-all in App.tsx still runs *after* the explicit routes.
 */
import React, { lazy, Suspense } from "react";

// --- Phase 3 organizer surfaces -----------------------------------

export const LazyToday = lazy(() => import("./pages/Today"));
export const LazyInbox = lazy(() => import("./pages/Inbox"));
export const LazyUpcoming = lazy(() => import("./pages/Upcoming"));
export const LazyAreas = lazy(() => import("./pages/Areas"));
export const LazyProjects = lazy(() => import("./pages/Projects"));
export const LazyCommsPage = lazy(() => import("./pages/Comms"));
export const LazyCanvas = lazy(() => import("./pages/Canvas"));

// --- Trust surfaces -----------------------------------------------

export const LazyDecisionLog = lazy(() => import("./pages/DecisionLog"));
export const LazyRetrospective = lazy(() => import("./pages/Retrospective"));
export const LazyAutonomyTelemetry = lazy(() => import("./pages/AutonomyTelemetry"));
// R1.10 (LA-022 / LA-024) — Context Inspector
export const LazyContextInspector = lazy(() => import("./pages/ContextInspector"));
// OperatorModel exports a named function `OperatorModelPage`, not default.
export const LazyOperatorModel = lazy(() =>
  import("./pages/OperatorModel").then((module) => ({ default: module.OperatorModelPage })),
);

// --- Admin pages --------------------------------------------------
// ChallengeQueue exports a named function `ChallengeQueuePage`, not default.
export const LazyChallengeQueue = lazy(() =>
  import("./pages/ChallengeQueue").then((module) => ({ default: module.ChallengeQueuePage })),
);
export const LazyApprovalQueue = lazy(() => import("./pages/ApprovalQueue"));
export const LazyAgentCapabilities = lazy(() => import("./pages/AgentCapabilities"));
export const LazyAgentEmail = lazy(() => import("./pages/AgentEmail"));
export const LazyAgentWallet = lazy(() => import("./pages/AgentWallet"));
export const LazyAudit = lazy(() => import("./pages/Audit"));
export const LazyBrowserAccess = lazy(() => import("./pages/BrowserAccess"));
export const LazyConfigEditor = lazy(() => import("./pages/ConfigEditor"));
export const LazyContextFiles = lazy(() => import("./pages/ContextFiles"));
export const LazyCredentials = lazy(() => import("./pages/Credentials"));
export const LazyDataSources = lazy(() => import("./pages/DataSources"));
export const LazyDevLoop = lazy(() => import("./pages/DevLoop"));
export const LazyEmailDrafts = lazy(() => import("./pages/EmailDrafts"));
export const LazyInjectionDefense = lazy(() => import("./pages/InjectionDefense"));
export const LazyMandates = lazy(() => import("./pages/Mandates"));
export const LazyMiddlewareConfig = lazy(() => import("./pages/MiddlewareConfig"));
export const LazyModels = lazy(() => import("./pages/Models"));
export const LazyOptimizer = lazy(() => import("./pages/Optimizer"));
export const LazyPluginManager = lazy(() => import("./pages/PluginManager"));
export const LazyRateLimits = lazy(() => import("./pages/RateLimits"));
export const LazyRelationshipGraph = lazy(() => import("./pages/RelationshipGraph"));
export const LazySecurityObservability = lazy(() => import("./pages/SecurityObservability"));
export const LazyServices = lazy(() => import("./pages/Services"));
export const LazySystemHealth = lazy(() => import("./pages/SystemHealth"));
export const LazySetupWizard = lazy(() => import("./pages/SetupWizard"));
export const LazyWorkspaceAccess = lazy(() => import("./pages/WorkspaceAccess"));
export const LazyWorkspaceZones = lazy(() => import("./pages/WorkspaceZones"));
export const LazyDashboard = lazy(() => import("./pages/Dashboard"));

// --- Suspense fallback -------------------------------------------

function PageLoading(): React.ReactElement {
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center">
      <div className="flex items-center gap-3 text-sm" style={{ color: "var(--c-text-secondary)" }}>
        <div
          className="h-4 w-4 animate-spin rounded-full border-2"
          style={{ borderColor: "var(--c-border)", borderTopColor: "var(--c-primary)" }}
        />
        Loading…
      </div>
    </div>
  );
}

/**
 * Wrap a lazy-loaded page in a Suspense boundary.
 *
 * Using a function rather than a JSX wrapper component keeps the route
 * declarations terse: `element={withSuspense(<LazyToday />)}`.
 */
export function withSuspense(node: React.ReactNode): React.ReactElement {
  return <Suspense fallback={<PageLoading />}>{node}</Suspense>;
}

// Re-export the route manifest so callers that ALREADY import from
// `routes` (App.tsx, etc.) keep working. New non-JSX consumers (Cmd+K
// palette builder, sidebar nav) should import directly from
// `./route-manifest` so they don't drag the lazy() React subtree into
// their dependency graph.
export { ROUTE_MANIFEST, type RouteEntry } from "./route-manifest";
